import React, { useCallback, useEffect, useState } from "react";
import $ from "jquery";
import { Link } from "react-router-dom";
import "../../../assets/css/overlay.css";
import Loader from "../../../components/loader";
import SnackbarAlert from "../../../components/SnackBarAlert";
import {
  getClassSimulation,
  playSimulation,
} from "../../../services/SsClassPublicService";
import { SsPublicSimulationDetail } from "../../../types/self-serve.public.types";
import {
  clearEnrolmentToken,
  getValidEnrolmentSession,
  onEnrolmentDisplaced,
} from "../../../utils/self.serve.enrolment-storage";
import { FooterWithHelpIcon } from "../../container/default-layout/footerWithHelpIcon";
import PublicHeader from "../public-pages/PublicHeader";
import ViewSimPage from "../simulation/view.sim.page";
import { CLOSED_ALERT, SsAlertState } from "./self.serve.alert";

/**
 * Public Self-Serve simulation detail — `/class/:slug/simulation/:simId` (ELS-671).
 * Uses the shared public header. Detail is loaded with the enrolment JWT and only for a sim
 * active on the class; any other sim id in the URL leaves the page blank. Play replaces the
 * current history entry with the server-returned `ssoPath` (no link/token in the URL), so Back
 * does not return to this page.
 */

/** 404: sim not active on the class. 400: bad sim id. Both leave the page blank. */
function isSimNotAvailable(err: any): boolean {
  const status = err?.response?.status;
  return status === 404 || status === 400;
}

/** Same document-access rules as the portal detail page (UX only; backend authorizes play). */
function hasDocumentAccess(docAccess: string | undefined, role: string | null): boolean {
  if (docAccess === "none") {
    return false;
  }
  if (docAccess === "everyone") {
    return true;
  }
  if (
    docAccess === "admin" &&
    (role === "Role_Super_Admin" || role === "Role_Admin")
  ) {
    return true;
  }
  return false;
}

function openDocument(event: React.MouseEvent<HTMLAnchorElement>): void {
  event.preventDefault();
  const el = event.currentTarget.getAttribute("data-name");
  if (el) {
    window.open("/document/" + el);
  }
}

function openFacilitatorDocument(event: React.MouseEvent<HTMLAnchorElement>): void {
  event.preventDefault();
  const el = event.currentTarget.getAttribute("data-name");
  if (el) {
    window.open(el);
  }
}

/**
 * Opens the play destination and drops this page from history.
 * Absolute SimBuilder URLs leave the SPA; portal paths stay in-app.
 */
function navigateToSsoPath(
  ssoPath: string,
  history: { replace: (path: string) => void }
): void {
  if (/^https?:\/\//i.test(ssoPath)) {
    window.location.replace(ssoPath);
    return;
  }
  if (ssoPath.startsWith("/")) {
    history.replace(ssoPath);
  }
}

const ClassLandingSimulationDetail: React.FC<any> = (props) => {
  const slug = props.match?.params?.slug;
  const rawSimId = Number(props.match?.params?.simId);
  const simId = Number.isInteger(rawSimId) && rawSimId > 0 ? rawSimId : 0;

  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  /** Sim id is not on this class (or not a valid id): render an empty content area. */
  const [notAvailable, setNotAvailable] = useState(false);
  const [simulation, setSimulation] = useState<SsPublicSimulationDetail | null>(
    null
  );
  const [alert, setAlert] = useState<SsAlertState>(CLOSED_ALERT);

  const goToLanding = useCallback(() => {
    if (!slug) {
      return;
    }
    props.history.replace(`/class/${encodeURIComponent(slug)}`);
  }, [slug, props.history]);

  const showError = useCallback((msg: string) => {
    setAlert({ open: true, type: "error", msg });
  }, []);

  // Sign this tab out when another student redeems the same class in another tab.
  useEffect(() => {
    if (!slug) {
      return;
    }
    return onEnrolmentDisplaced(slug, () => {
      clearEnrolmentToken(slug);
      goToLanding();
    });
  }, [slug, goToLanding]);

  // On mount: require enrolment JWT → GET class-scoped sim detail. The backend checks the
  // enrolment and that the sim is active on the class; Play re-checks both.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!slug) {
        return;
      }

      const session = getValidEnrolmentSession(slug);
      if (!session?.enrolmentToken) {
        goToLanding();
        return;
      }

      if (!simId) {
        setSimulation(null);
        setNotAvailable(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setNotAvailable(false);
      try {
        const detail = await getClassSimulation(slug, session.enrolmentToken, simId);
        if (cancelled) {
          return;
        }
        setSimulation(detail);
      } catch (err: any) {
        if (cancelled) {
          return;
        }
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          clearEnrolmentToken(slug);
          goToLanding();
          return;
        }
        if (isSimNotAvailable(err)) {
          setSimulation(null);
          setNotAvailable(true);
          return;
        }
        showError(
          err?.userMessage || "We could not open that simulation. Please try again."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [slug, simId, goToLanding, showError]);

  // ViewSimPage renders the preview overlay but leaves the open/close wiring to its host page
  // (same as PublicSimDetail). Bind once the detail is on screen and a preview exists.
  const preview = !loading ? simulation?.preview : undefined;
  useEffect(() => {
    if (!preview) {
      return;
    }

    // display video overlay and play
    $(".poster").on("click", function () {
      $(this).parent().prev().fadeIn();
      const video = document.getElementsByTagName("video");
      if (video[0]) video[0].play();
    });

    // hide video overlay
    $(".simulation-blanket").on("click", function () {
      $(this).fadeOut();
      const video = document.getElementsByTagName("video");
      if (video[0]) video[0].pause();
    });

    // stop prop for video controls
    $(".simulation-video").on("click", function (e) {
      e.stopPropagation();
    });

    return () => {
      $(".poster").off("click");
      $(".simulation-blanket").off("click");
      $(".simulation-video").off("click");
    };
  }, [preview]);

  const handlePlay = useCallback(async () => {
    if (!slug || !simId || playing) {
      return;
    }
    const session = getValidEnrolmentSession(slug);
    if (!session?.enrolmentToken) {
      goToLanding();
      return;
    }

    setPlaying(true);
    try {
      const { ssoPath } = await playSimulation(slug, session.enrolmentToken, {
        simId,
      });
      if (!ssoPath) {
        showError("We could not start that simulation. Please try again.");
        return;
      }
      navigateToSsoPath(ssoPath, props.history);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        clearEnrolmentToken(slug);
        goToLanding();
        return;
      }
      // Sim was removed from the class after the page loaded.
      if (isSimNotAvailable(err)) {
        setSimulation(null);
        setNotAvailable(true);
        return;
      }
      showError(
        err?.userMessage || "We could not start that simulation. Please try again."
      );
    } finally {
      setPlaying(false);
    }
  }, [slug, simId, playing, goToLanding, showError, props.history]);

  return (
    <div className="page-container-sim-detail">
      <link media="all" href="/view_sim_page_design.css" rel="stylesheet" />
      <PublicHeader>
      <div id="content">
            {simulation && !loading ? (
              <ViewSimPage
                simName={simulation.simName}
                simDuration={simulation.simDuration}
                message=""
                detailText={simulation.detailText}
                handleAlertDialog={() => undefined}
                loggedIn={false}
                validUser={true}
                role={null}
                createdBy=""
                email=""
                playSimulations={handlePlay}
                thumbnail={simulation.thumbnail}
                preview={simulation.preview}
                hasDocumentAccess={hasDocumentAccess}
                openDocument={openDocument}
                docFile1={simulation.docFile1}
                docAccess1={simulation.docAccess1}
                docName1={simulation.docName1}
                docFile2={simulation.docFile2}
                docAccess2={simulation.docAccess2}
                docName2={simulation.docName2}
                docFile3={simulation.docFile3}
                docAccess3={simulation.docAccess3}
                docName3={simulation.docName3}
                docFile4={simulation.docFile4}
                docAccess4={simulation.docAccess4}
                docName4={simulation.docName4}
                docFile5={simulation.docFile5}
                docAccess5={simulation.docAccess5}
                docName5={simulation.docName5}
                simFacilitatorDocumentsListBySimId={
                  simulation.simFacilitatorDocumentsListBySimId
                }
                openFacilitatorDocument={openFacilitatorDocument}
                simId={simulation.simId}
                accId={0}
              />
            ) : null}
            {!loading && !simulation && !notAvailable ? (
              <div className="content-view-page" role="alert">
                <p>
                  This simulation could not be opened. Return to{" "}
                  {slug ? (
                    <Link
                      to={`/class/${encodeURIComponent(slug)}/access-simulation`}
                    >
                      your simulations
                    </Link>
                  ) : (
                    "your simulations"
                  )}{" "}
                  and try again.
                </p>
              </div>
            ) : null}
      </div>
      </PublicHeader>

      {(loading || playing) && (
        <div
          className="overlay"
          role="status"
          aria-live="polite"
          aria-label={playing ? "Starting simulation" : "Loading"}
        >
          <Loader loading={true} />
        </div>
      )}

      <FooterWithHelpIcon />
      <SnackbarAlert
        openAlert={alert.open}
        setOpenAlert={() => setAlert(CLOSED_ALERT)}
        type={alert.type}
        msg={alert.msg}
      />
    </div>
  );
};

export default ClassLandingSimulationDetail;
